import { parseDate } from "@/utilities/datetime";
import type { PickemsMarket } from "../models/pickems";
import type { Always } from "@/types/utilities";

export const getPickemsMarketBetStatus = (
  marketStatus: PickemsMarket["status"],
) => {
  if (typeof marketStatus !== "string" || !marketStatus) {
    return;
  }

  switch (marketStatus) {
    case "DEACTIVATED":
      return "LIVE";
    case "SETTLED":
    case "CANCELLED":
      return "RESULTED";
    case "ACTIVE":
      // this will be shown if a market has passed its scheduled start time but has not been deactivated yet
      return "WAITING";
    default:
      return marketStatus;
  }
};

export const transformPickemsMarketEvent = (
  event: PickemsMarket["event"],
  market: PickemsMarket,
) => {
  // Build a record of competitors to store at the top-level of the event object
  const competitors = Object.values(market.outcomes || {}).reduce<
    Record<
      string,
      Always<NonNullable<PickemsMarket["outcomes"]>[string]["competitor"]>
    >
  >((acc, outcome) => {
    if (outcome.competitorId && outcome.competitor) {
      acc[outcome.competitorId] = outcome.competitor;
    }
    return acc;
  }, {});

  const out = {
    ...event,
    competitors,
    scheduledStartTime: parseDate(event.scheduledStartTime),
  };

  return out;
};

export const transformPickemsMarkets = (markets: PickemsMarket[]) => {
  const out = [];

  for (const market of markets) {
    const { event, scheduledStartTime, outcomes, ...restMarket } = market;

    // Add a new odds property to each outcome, derived from the pick's points
    const newOutcomeEntries = Object.entries(outcomes ?? {}).map(
      ([outcomeId, outcome]) => {
        return [
          outcomeId,
          {
            ...outcome,
            odds: outcome.points,
          },
        ];
      },
    );

    out.push({
      ...restMarket,
      event: transformPickemsMarketEvent(event, market),
      betStatus: getPickemsMarketBetStatus(restMarket.status),
      outcomes: Object.fromEntries(newOutcomeEntries),
      scheduledStartTime: parseDate(event.scheduledStartTime),
    });
  }

  return out;
};
